import React, { useState, useMemo, useCallback } from "react";
import { Container, Row, Col, Spinner, Button, Offcanvas, Badge } from "react-bootstrap";
import { motion, AnimatePresence } from "framer-motion";
import { FaSearch, FaChevronRight, FaInfoCircle, FaAward, FaFilter } from "react-icons/fa";
import DOMPurify from "dompurify";

// Shared Components & Hooks
import Header from "../components/Header";
import Footer from "../components/Footer";
import useProgramData from "../hooks/useProgramData";

const Scholarships = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [activeIdx, setActiveIdx] = useState(0);
  const [showDrawer, setShowDrawer] = useState(false); // Mobile Drawer

  // Data Fetching
  const { data: apiData, loading } = useProgramData("scholarshipsData");

  // Memoized scholarship list from API
  const allScholarships = useMemo(() => {
    return apiData?.[0]?.scholarships || [];
  }, [apiData]);

  // Performance-optimized filtering
  const filteredScholarships = useMemo(() => {
    return allScholarships.filter(s =>
      s.title.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [allScholarships, searchTerm]);

  const current = filteredScholarships[activeIdx] || null;

  // Sanitization
  const sanitizedDescription = useMemo(() => {
    if (!current?.description) return "";
    return DOMPurify.sanitize(current.description);
  }, [current]);

  const handleSearch = useCallback((e) => {
    setSearchTerm(e.target.value);
    setActiveIdx(0);
  }, []);

  if (loading) return (
    <div className="premium-loader">
      <Spinner animation="grow" variant="primary" />
      <p className="mt-3 fw-bold letter-spacing-2 text-uppercase">Syncing Opportunities</p>
    </div>
  );

  return (
    <div className="portal-master-wrapper">
      <Header />

      {/* Cinematic Hero Section */}
      <div className="explorer-hero position-relative overflow-hidden">
        <div className="hero-pattern-overlay"></div>
        <Container>
          <motion.div 
            initial={{ opacity: 0, y: 30 }} 
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            className="hero-content text-center position-relative" style={{ zIndex: 2 }}
          >
            <motion.div 
              animate={{ y: [0, -10, 0] }}
              transition={{ repeat: Infinity, duration: 4, ease: "easeInOut" }}
              className="hero-icon-circle mb-4 shadow-lg"
            >
              <FaAward />
            </motion.div>
            <h1 className="hero-title display-3 fw-900 mb-3">Scholarships & <span className="text-warning">Aid</span></h1>
            <p className="hero-subtitle mb-5 lead">Ensuring that deserving talent at Sukkur IBA University is never held back by financial constraints.</p>
            
            <Button 
                variant="primary" 
                className="rounded-pill px-5 py-3 d-lg-none nav-hint-pulse shadow-lg border-2 border-white fw-bold"
                onClick={() => setShowDrawer(true)}
            >
                <FaFilter className="me-2"/> Select Program
            </Button>
          </motion.div>
        </Container>
      </div>

      {/* --- Standardized Search Sub-Nav --- */}
      <section className="portal-sub-nav py-4 shadow-sm bg-white sticky-top" style={{ top: '0', zIndex: 1020 }}>
        <Container>
          <Row className="align-items-center">
            <Col md={5}>
              <h1 className="h4 fw-900 text-navy m-0">Financial <span className="text-primary">Support</span></h1>
            </Col>
            <Col md={7}>
              <div className="d-flex gap-3 justify-content-end align-items-center mt-3 mt-md-0">
                <div className="search-pill-container flex-grow-1">
                  <FaSearch className="search-icon" />
                  <input type="text" placeholder="Search opportunities..." value={searchTerm} onChange={handleSearch} />
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* --- Optimized Content Section --- */}
      <section className="portal-viewport bg-light-soft py-5">
        <Container className="main-portal-container section-standard-spacing position-relative" style={{ zIndex: 10 }}>
          <Row className="g-4">
            {/* Sidebar Navigation */}
            <Col lg={4} className="d-none d-lg-block">
              <div className="sticky-sidebar">
                <div className="sidebar-card border-0 shadow-sm">
                  <div className="card-label">AVAILABLE PROGRAMS</div>
                  <div className="program-list custom-scrollbar">
                    {filteredScholarships.map((sch, i) => (
                      <button 
                        key={i} 
                        className={`prog-item w-100 d-flex align-items-center justify-content-between p-3 border-0 bg-transparent transition-all ${activeIdx === i ? 'is-active' : ''}`} 
                        onClick={() => setActiveIdx(i)}
                      >
                        <div className="d-flex align-items-center gap-3">
                          <div className={`icon-circle-mini ${activeIdx === i ? 'active' : ''}`}>
                            <FaAward />
                          </div>
                          <div className="text-start">
                              <div className="name fw-bold mb-0">{sch.title}</div>
                              <div className="stats-tag">Active Opportunity</div>
                          </div>
                        </div>
                        <FaChevronRight className={`chevron-mini ${activeIdx === i ? 'visible' : ''}`} />
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </Col>

            {/* Details Viewport */}
            <Col lg={8}>
              <AnimatePresence mode="wait">
                {current ? (
                  <motion.div 
                    key={current.title} 
                    initial={{ opacity: 0, x: 10 }} 
                    animate={{ opacity: 1, x: 0 }} 
                    exit={{ opacity: 0, x: -10 }}
                    className="details-card bg-white p-4 p-lg-5 rounded-4 shadow-sm"
                  >
                  <div className="section-intro mb-5">
                    <div className="category-tag mb-2">Funding Detail</div>
                    <h2 className="portal-heading text-navy display-5 mb-3">{current.title}</h2>
                    <div className="ivy-divider mb-4" />
                    <div className="d-flex flex-wrap gap-2 mb-4">
                        <Badge bg="soft-primary" className="text-primary px-3 py-2 rounded-pill fw-bold">Academic Year 2026</Badge>
                        <Badge bg="soft-warning" className="text-dark px-3 py-2 rounded-pill fw-bold">Fully Funded</Badge>
                    </div>
                  </div>

                  <div className="scholarship-content editorial-text-body">
                    <div 
                      className="rich-text-wrapper"
                      dangerouslySetInnerHTML={{ __html: sanitizedDescription }} 
                    />
                  </div>

                    <div className="mt-5 p-4 rounded-4 bg-light d-flex flex-wrap align-items-center justify-content-between gap-3">
                      <div>
                        <h5 className="fw-bold text-navy mb-1">Ready to Apply?</h5>
                        <p className="small text-muted mb-0">Cross-check eligibility before submission.</p>
                      </div>
                      <div className="d-flex gap-2">
                         <Button variant="outline-primary" className="rounded-pill px-4 fw-bold">Inquiry</Button>
                         <Button variant="primary" className="rounded-pill px-4 fw-bold">Apply Now</Button>
                      </div>
                    </div>
                  </motion.div>
                ) : (
                  <div className="text-center py-5 bg-white rounded-4 shadow-sm">
                    <FaInfoCircle size={40} className="text-muted mb-3 opacity-20" />
                    <h4 className="text-muted">No Results Found</h4>
                  </div>
                )}
              </AnimatePresence>
            </Col>
          </Row>
        </Container>
      </section>

      {/* --- MOBILE SCHOLARSHIP DRAWER --- */}
      <Offcanvas show={showDrawer} onHide={() => setShowDrawer(false)} placement="bottom" className="h-50 rounded-top-4">
        <Offcanvas.Header closeButton className="border-bottom">
          <Offcanvas.Title className="fw-bold text-navy">Select Scholarship</Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body className="p-0">
          <div className="program-list-mobile">
            {filteredScholarships.map((sch, i) => (
                <button 
                key={i} 
                className={`prog-item mobile-item w-100 d-flex align-items-center justify-content-between p-3 border-0 bg-transparent transition-all ${activeIdx === i ? 'is-active' : ''}`} 
                onClick={() => { setActiveIdx(i); setShowDrawer(false); }}
                >
                <div className="d-flex align-items-center gap-3">
                    <div className={`icon-circle-mini ${activeIdx === i ? 'active' : ''}`}>
                         <FaAward />
                    </div>
                    <div className="text-start">
                        <div className="name fw-bold mb-0 text-dark">{sch.title}</div>
                        <div className="stats-tag">Select Program</div>
                    </div>
                </div>
                {activeIdx === i && <FaChevronRight className="chevron-mini visible" />}
                </button>
            ))}
          </div>
        </Offcanvas.Body>
      </Offcanvas>

      <Footer />

      <style>{`
        .portal-master-wrapper { background-color: #fff; min-height: 100vh; font-family: 'Inter', sans-serif; }
        .bg-light-soft { background-color: #f8faff; }
        .fw-900 { font-weight: 900; }
        .text-navy { color: #002b5a; }
        .bg-soft-primary { background-color: #eff6ff; }
        .bg-soft-warning { background-color: #fff7ed; }
        .ls-1 { letter-spacing: 1px; }

        /* Typography Sync with Homepage */
        .category-tag { color: #0055a4; font-weight: 800; text-transform: uppercase; font-size: 0.85rem; letter-spacing: 2px; }
        .portal-heading { font-weight: 900; letter-spacing: -1px; }
        .ivy-divider { width: 60px; height: 5px; background: #0055a4; border-radius: 10px; }
        .editorial-text-body { font-size: 1.1rem; line-height: 1.6; color: #475569; }

        .explorer-hero {
          background: #001D3D;
          background: linear-gradient(135deg, #000814 0%, #001D3D 100%);
          padding: 60px 0 100px 0;
          color: white;
        }

        .hero-pattern-overlay {
            position: absolute; inset: 0;
            background-image: radial-gradient(rgba(255,255,255,0.05) 1px, transparent 1px);
            background-size: 30px 30px;
            opacity: 0.4;
        }

        .hero-icon-circle {
          width: 80px; height: 80px; background: rgba(255,255,255,0.08);
          border-radius: 20px; display: flex; align-items: center; justify-content: center;
          margin: 0 auto; font-size: 2.5rem; color: #FFC300;
          border: 1px solid rgba(255,255,255,0.15);
          backdrop-filter: blur(10px);
        }

        .hero-title { font-weight: 900; letter-spacing: -1px; }
        .hero-subtitle { opacity: 0.85; max-width: 700px; margin: 0 auto; }

        .main-portal-container { margin-top: 0; padding-bottom: 100px; position: relative; z-index: 10; }
        
        .search-pill-container {
          background: #f1f4f9; border-radius: 50px; padding: 10px 25px;
          display: flex; align-items: center; transition: 0.3s;
        }
        .search-pill-container:focus-within { background: #fff; box-shadow: 0 10px 25px rgba(0,0,0,0.05); border: 1px solid #0055a4; }
        .search-pill-container input { border: none; background: transparent; outline: none; width: 100%; margin-left: 12px; font-weight: 500; color: #002b5a; }
        .search-icon { color: #94a3b8; }

        /* Sidebar Styling */
        .sticky-sidebar { position: sticky; top: 120px; z-index: 100; transition: top 0.3s; }
        .sidebar-card { background: #fff; border-radius: 24px; overflow: hidden; display: flex; flex-direction: column; max-height: 75vh; border: 1px solid #e2e8f0 !important; }
        
        .card-label { 
          padding: 18px 25px; background: #002b5a; color: white; 
          font-weight: 800; font-size: 0.75rem; letter-spacing: 2px; text-transform: uppercase;
        }

        .program-list { overflow-y: auto; flex: 1; }
        .custom-scrollbar::-webkit-scrollbar { width: 5px; }
        .custom-scrollbar::-webkit-scrollbar-thumb { background: #e2e8f0; border-radius: 10px; }

        .prog-item {
          transition: 0.3s;
          border-bottom: 1px solid #f1f5f9 !important;
        }
        .prog-item:hover { background-color: #f8fafc !important; }
        .prog-item.is-active {
          background-color: #002b5a !important;
          border-left: 6px solid #ffc300 !important;
        }
        .prog-item.is-active .name { color: white !important; }
        .prog-item.is-active .stats-tag { color: rgba(255,255,255,0.7) !important; }
        .prog-item.is-active .icon-circle-mini { background: rgba(255,255,255,0.1); border-color: rgba(255,255,255,0.2); color: white; }

        .icon-circle-mini {
          width: 42px; height: 42px; background: #f8fafc; border-radius: 12px;
          display: flex; align-items: center; justify-content: center;
          font-size: 1.1rem; color: #64748b; transition: 0.3s;
          border: 1px solid #e2e8f0;
        }
        .icon-circle-mini.active { background: #002b5a; color: #fff; border-color: #002b5a; }

        .stats-tag { font-size: 0.75rem; color: #94a3b8; font-weight: 600; }
        .chevron-mini { font-size: 0.8rem; color: #cbd5e0; opacity: 0; transition: 0.3s; transform: translateX(-10px); }
        .chevron-mini.visible { opacity: 1; transform: translateX(0); color: #0055a4; }
        .prog-item.is-active .chevron-mini.visible { color: #ffc300; }

        .details-card { border-radius: 24px !important; border: 1px solid #e2e8f0 !important; }

        .rich-text-wrapper h5 { color: #002b5a; font-weight: 800; margin-top: 1.5rem; margin-bottom: 1rem; }
        .rich-text-wrapper p { margin-bottom: 1.25rem; }
        .rich-text-wrapper ul { padding-left: 1.2rem; margin-bottom: 1.5rem; }
        .rich-text-wrapper li { margin-bottom: 0.5rem; position: relative; list-style: none; }
        .rich-text-wrapper li::before { content: "•"; color: #0055a4; font-weight: bold; position: absolute; left: -1.2rem; }

        @media (max-width: 991px) {
          .section-standard-spacing { padding: 30px 0; }
          .main-portal-container { margin-top: 0px; }
          .explorer-hero { padding: 30px 0 60px 0; }
          .hero-title { font-size: 1.8rem; letter-spacing: -1px; }
          .hero-subtitle { font-size: 0.9rem; margin-bottom: 1.5rem !important; }
          .category-tag { font-size: 0.7rem; letter-spacing: 1.5px; }
          .hero-icon-circle { width: 50px; height: 50px; font-size: 1.6rem; }
          .details-card { padding: 1rem !important; }
          .portal-heading { font-size: 1.6rem; word-break: break-word; letter-spacing: -1px; }
          
          .program-list-mobile .prog-item.mobile-item { border-bottom: 1px solid #f1f5f9 !important; padding: 15px; }
          .program-list-mobile .prog-item.mobile-item.is-active { 
            background: #002b5a !important; 
            color: white !important; 
            border-left: 5px solid #ffc300 !important; 
          }
          .program-list-mobile .prog-item.mobile-item.is-active .name { color: white !important; }
          .program-list-mobile .prog-item.mobile-item.is-active .stats-tag { color: rgba(255,255,255,0.7) !important; }
          .program-list-mobile .prog-item.mobile-item.is-active .icon-circle-mini { background: rgba(255,255,255,0.1); border-color: rgba(255,255,255,0.2); color: white; }
          .sticky-sidebar { position: relative !important; top: 0 !important; }
        }

        /* Navigation Hint Pulse */
        @keyframes hintPulse {
          0% { box-shadow: 0 0 0 0 rgba(255, 195, 0, 0.4); }
          70% { box-shadow: 0 0 0 15px rgba(255, 195, 0, 0); }
          100% { box-shadow: 0 0 0 0 rgba(255, 195, 0, 0); }
        }
        .nav-hint-pulse {
          animation: hintPulse 2s infinite;
          background: #002b5a !important;
          border-color: #ffc300 !important;
        }
      `}</style>
    </div>
  );
};

export default Scholarships;
